// ArchIE headless run orchestration (fn-69.3, R4/R5).
//
// The side-effectful glue that drives the fn-69.2 headless driver from
// the browser: upload → create-only session (matter-less, mounted file)
// → wait out the fn-67 bootstrap turn → kickoff → poll `/events`,
// classify, and resolve restricted-form gates per the per-mode policy.
// ALL traffic goes through the BFF (cookie auth) — never CP-direct.
//
// The driver (`../../headless`) owns the *pure* logic (parser, policy,
// form-data, bootstrap sequencing, approval resolution, event
// classification). This hook is the thin imperative shell + the resumable
// poll loop: in `attended` mode it PAUSES on a restricted form (surfacing
// a card) and `resolveGate` resumes the same loop; in `lights-out` it
// auto-approves and never pauses.

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { bffEndpoints } from '../../auth/bff-origin.js';
import {
  type HeadlessApprovalRow,
  type HeadlessCreateInput,
  type HeadlessTransport,
  type ParseFallbackReason,
  bareToolName,
  bootstrapAndKickoff,
  buildHeadlessKickoff,
  classifyEvent,
  createHeadlessTransport,
  decide,
  formDataForGate,
  planFormDataForApproval,
  resolvePendingApproval,
  toHeadlessClient,
} from '../../headless/index.js';
import { createVanillaTrpcClient, retryableRefreshFetch } from '../../trpc-client.js';
import {
  type RunResult,
  type RunState,
  extractMountedRealPaths,
  initialRunState,
  isTerminalPhase,
  observeCreateMatter,
  runReducer,
  toGateView,
} from './run-machine.js';
import type { IntakeSubmit } from './view.js';

/** Where the uploaded intake document is mounted (CP re-stamps `real_path`). */
const MOUNT_PATH = '/intake/document';
/** Event-poll cadence for the run loop. */
const POLL_MS = 1_500;
/** Hard ceiling on a single run before we surface a graceful timeout. */
const MAX_RUN_MS = 10 * 60 * 1000;
/** Session statuses that END a run (no more progress possible). */
const TERMINAL_SESSION: ReadonlySet<string> = new Set([
  'completed',
  'failed',
  'cancelled',
  'expired',
]);

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** A friendly one-liner for a parse fallback (debug breadcrumb, not shown raw). */
function fallbackNote(reason: ParseFallbackReason): string {
  return `non-conforming agent turn (${reason})`;
}

/**
 * The terminal result for a run that reached `done`/`completed`: a real
 * created matter when one was observed, else a graceful unavailable state
 * (R5) — NEVER a false "created" from the review draft.
 */
function terminalResult(ctx: LoopCtx): RunResult {
  if (ctx.createdMatter !== undefined) {
    return { kind: 'created', matter: ctx.createdMatter };
  }
  return {
    kind: 'unavailable',
    reason: 'the run finished without an observed create_matter result',
    lastPhase: 'done',
  };
}

interface LoopCtx {
  readonly transport: HeadlessTransport;
  readonly sessionId: string;
  readonly mode: RunState['mode'];
  readonly handled: Set<string>;
  /**
   * Wall-clock deadline for AGENT progress. Refreshed each time the loop
   * resumes after a human approval (attended mode) so a long review pause
   * is NOT counted against the agent's run budget (it would otherwise make
   * the resume immediately "time out").
   */
  deadline: number;
  /**
   * Per-run identity. Every `start`/`reset` bumps the hook's run
   * generation; an in-flight loop whose `gen` no longer matches is STALE
   * and must not dispatch into the (possibly new) run. Closes the
   * reset-vs-in-flight-await race.
   */
  readonly gen: number;
  /**
   * Event ids already processed. The MA events endpoint REJECTS `after_id`
   * (`ma-client.ts` getEvents — the `afterId` shim wire-emits `after_id=`
   * which 400s on anthropic-direct → the CP returns 500), so we re-fetch
   * the full page each poll and dedup client-side, mirroring the proven
   * deployed e2e harness. Never paginate with `afterId` here.
   */
  readonly seen: Set<string>;
  /**
   * The created matter — sourced from the `create_matter` gate args and
   * ENRICHED by the create_matter tool RESULT (the backend-assigned id),
   * NOT the `review_matter` draft. `undefined` means no create was
   * observed → the run finishes in a graceful unavailable state, never a
   * false "created".
   */
  createdMatter: Record<string, unknown> | undefined;
  /** tool_use_id of the in-flight `create_matter` call, to match its result. */
  createMatterToolUseId: string | undefined;
  /** The matter confirmed at the `review_matter` gate — the rich source for
   * the "Matter created" panel (overlaid by the create result's id). */
  reviewedMatter: Record<string, unknown> | undefined;
}

export interface UseHeadlessRun {
  readonly state: RunState;
  readonly uploading: boolean;
  readonly uploadError: string | undefined;
  readonly uploadedFile: { readonly id: string; readonly name: string } | undefined;
  readonly gateBusy: boolean;
  readonly uploadFile: (file: File) => void;
  readonly start: (input: IntakeSubmit) => void;
  readonly resolveGate: (
    decision: 'approved' | 'rejected',
    override?: Record<string, unknown>,
  ) => void;
  readonly reset: () => void;
}

export function useHeadlessRun(): UseHeadlessRun {
  const [state, dispatch] = useReducer(runReducer, undefined, () => initialRunState('attended'));
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>(undefined);
  const [uploadedFile, setUploadedFile] = useState<{ id: string; name: string } | undefined>(
    undefined,
  );
  const [gateBusy, setGateBusy] = useState(false);

  // Stable BFF clients. The vanilla client is the CP-proxy surface; the
  // transport is its driver-shaped adapter.
  const vanillaClient = useMemo(
    () => createVanillaTrpcClient({ trpcUrl: bffEndpoints().trpcUrl }),
    [],
  );
  const transport = useMemo<HeadlessTransport>(
    () => createHeadlessTransport(toHeadlessClient(vanillaClient)),
    [vanillaClient],
  );

  const loopRef = useRef<LoopCtx | null>(null);
  const pendingRowRef = useRef<HeadlessApprovalRow | null>(null);
  // Monotonic per-run token; a loop whose captured `gen` ≠ current is stale.
  const runGenRef = useRef(0);
  // Monotonic per-UPLOAD token; a stale upload completing after reset / a new
  // upload must not repopulate `uploadedFile` (which would mount an old doc).
  const uploadGenRef = useRef(0);
  const mountedRef = useRef(true);

  // A loop iteration is stale if the component unmounted OR a newer run
  // (start/reset) superseded it. Checked after every await before any
  // dispatch / ref mutation.
  const isStale = useCallback(
    (gen: number): boolean => !mountedRef.current || runGenRef.current !== gen,
    [],
  );

  // Stop dispatching into an unmounted tree.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /** Post one approval decision, building the per-tool `form_data`. */
  const applyDecision = useCallback(
    async (
      row: HeadlessApprovalRow,
      decision: 'approved' | 'rejected',
      override?: Record<string, unknown>,
    ): Promise<void> => {
      const ctx = loopRef.current;
      if (ctx === null) {
        return;
      }
      let formData: Record<string, unknown> | undefined;
      if (decision === 'approved') {
        formData = formDataForGate(
          { tool_name: row.tool_name, tool_args: row.tool_args },
          override !== undefined ? { matterOverride: override } : {},
        );
        // Stash the confirmed matter from the review gate so a successful
        // create renders a rich "Matter created" panel (name/type/parties),
        // overlaid by the backend id from the create result.
        if (bareToolName(row.tool_name) === 'review_matter' && formData !== undefined) {
          ctx.reviewedMatter = formData;
        }
        // NOTE: approving create_matter is only a REQUEST — it does NOT make
        // the matter created. `ctx.createdMatter` is set ONLY from the
        // observed create_matter tool RESULT (`observeCreateMatter`), so a
        // failed/missing/non-JSON result finishes in the graceful
        // unavailable state, never a false "created".
      }
      await transport.decide({
        sessionId: ctx.sessionId,
        approvalId: row.id,
        decision,
        ...(formData !== undefined ? { formData } : {}),
        ...(decision === 'rejected' ? { denyMessage: 'Rejected by operator' } : {}),
      });
      ctx.handled.add(row.id);
    },
    [transport],
  );

  /** Drive the poll loop until completion or an attended pause. Resumable. */
  const pump = useCallback(async (): Promise<void> => {
    const ctx = loopRef.current;
    if (ctx === null) {
      return;
    }
    try {
      while (Date.now() < ctx.deadline) {
        if (isStale(ctx.gen)) {
          return;
        }
        // Full-page re-fetch + client-side dedup. The events endpoint
        // rejects `after_id` (see LoopCtx.seen) — incremental paging here
        //500s and strands the run at "starting". `limit: 1000` covers any
        // single matter-intake run; `seen` makes re-fetch idempotent.
        const events = await transport.getEvents(ctx.sessionId, {
          order: 'asc',
          limit: 1000,
        });
        if (isStale(ctx.gen)) {
          return;
        }
        let sawGate = false;
        for (const ev of events) {
          if (ctx.seen.has(ev.id)) {
            continue;
          }
          ctx.seen.add(ev.id);
          observeCreateMatter(ctx, ev);
          for (const view of classifyEvent(ev, 'headless')) {
            if (view.kind === 'status') {
              dispatch({ type: 'STATUS', status: view.status });
              if (isTerminalPhase(view.status.phase)) {
                if (view.status.phase.trim().toLowerCase() === 'done') {
                  dispatch({ type: 'DONE', result: terminalResult(ctx) });
                } else {
                  dispatch({ type: 'ERROR', reason: `agent reported "${view.status.phase}"` });
                }
                return;
              }
            } else if (view.kind === 'fallback') {
              dispatch({ type: 'FALLBACK' });
              void fallbackNote(view.reason as ParseFallbackReason);
            } else if (view.kind === 'tool') {
              dispatch({ type: 'TOOL', tool: view.tool, kind: view.toolKind });
            } else if (view.kind === 'gate') {
              sawGate = true;
            }
          }
        }

        const status = await transport.getSessionStatus(ctx.sessionId);
        if (isStale(ctx.gen)) {
          return;
        }
        if (TERMINAL_SESSION.has(status)) {
          if (status === 'completed') {
            dispatch({ type: 'DONE', result: terminalResult(ctx) });
          } else {
            dispatch({ type: 'ERROR', reason: `session ${status}` });
          }
          return;
        }

        if (sawGate || status === 'awaiting_approval') {
          const row = await resolvePendingApproval({
            transport,
            sessionId: ctx.sessionId,
            excludeApprovalIds: ctx.handled,
          });
          if (isStale(ctx.gen)) {
            return;
          }
          if (row !== null) {
            const action = decide({ tool_name: row.tool_name, ack_kind: row.ack_kind }, ctx.mode);
            if (action === 'ask') {
              pendingRowRef.current = row;
              dispatch({ type: 'GATE_PENDING', gate: toGateView(row) });
              return; // pause — `resolveGate` resumes the loop
            }
            // auto-approve / auto-answer-default — but FAIL CLOSED if an
            // immediate-custom tool's payload can't be synthesized (a
            // malformed `ask_user_question`): surface it for human review
            // rather than posting `approved` with no form_data (an invalid
            // lights-out approval). `planFormDataForApproval` makes the
            // "no default answers → never auto-approve" contract explicit.
            const plan = planFormDataForApproval({
              tool_name: row.tool_name,
              tool_args: row.tool_args,
            });
            if (plan.kind === 'needs-human') {
              pendingRowRef.current = row;
              dispatch({ type: 'GATE_PENDING', gate: toGateView(row) });
              return; // pause — fail closed to a human
            }
            await applyDecision(row, 'approved');
            if (isStale(ctx.gen)) {
              return;
            }
            continue; // re-poll immediately
          }
        }

        await sleep(POLL_MS);
      }
      if (isStale(ctx.gen)) {
        return;
      }
      dispatch({ type: 'ERROR', reason: 'timed out waiting for the agent' });
    } catch (err) {
      if (isStale(ctx.gen)) {
        return;
      }
      dispatch({ type: 'ERROR', reason: (err as Error).message ?? 'run failed' });
    }
  }, [transport, applyDecision, isStale]);

  const start = useCallback(
    async (input: IntakeSubmit): Promise<void> => {
      // New run identity — supersedes any in-flight loop from a prior run.
      const gen = ++runGenRef.current;
      loopRef.current = null;
      pendingRowRef.current = null;
      setGateBusy(false);
      dispatch({ type: 'START', mode: input.mode });

      // Two trust-distinct kickoff shapes (impl-review round 2): pasted text
      // is UNTRUSTED document data → goes inside the kickoff's fence; a mounted
      // file is read by the agent itself → a TRUSTED mounted-path directive
      // ABOVE the fence (baking the file-read instruction into `document` would
      // place it below the positional untrusted marker, so a compliant agent
      // would ignore its own read instruction).
      const kickoff =
        input.documentText.length > 0
          ? buildHeadlessKickoff({ document: input.documentText })
          : buildHeadlessKickoff({
              mountedPath: `/mnt/session/uploads${MOUNT_PATH}`,
            });
      const create: HeadlessCreateInput = {
        agentId: input.agentId,
        metadata: { title: 'ArchIE headless intake' },
        ...(input.fileId !== undefined
          ? { mountedFiles: [{ fileId: input.fileId, mountPath: MOUNT_PATH }] }
          : {}),
      };

      try {
        const boot = await bootstrapAndKickoff({ transport, create, kickoff });
        if (isStale(gen)) {
          return;
        }
        if (boot.outcome === 'bootstrap-unavailable') {
          dispatch({ type: 'ERROR', reason: `session not sendable (${boot.status})` });
          return;
        }

        // R5 proof: read back the CP-re-stamped mount paths (non-fatal).
        let realPaths: string[] = [];
        try {
          const row = (await vanillaClient.runs.byId.query({ sessionId: boot.sessionId })) as {
            metadata?: unknown;
          };
          realPaths = extractMountedRealPaths(row.metadata);
        } catch {
          // metadata read is advisory; don't fail the run on it.
        }
        if (isStale(gen)) {
          return;
        }
        dispatch({
          type: 'SESSION_CREATED',
          sessionId: boot.sessionId,
          mountedRealPaths: realPaths,
        });

        loopRef.current = {
          transport,
          sessionId: boot.sessionId,
          mode: input.mode,
          handled: new Set<string>(),
          deadline: Date.now() + MAX_RUN_MS,
          gen,
          seen: new Set<string>(),
          createdMatter: undefined,
          createMatterToolUseId: undefined,
          reviewedMatter: undefined,
        };
        await pump();
      } catch (err) {
        if (isStale(gen)) {
          return;
        }
        dispatch({ type: 'ERROR', reason: (err as Error).message ?? 'failed to start run' });
      }
    },
    [transport, vanillaClient, pump, isStale],
  );

  const resolveGate = useCallback(
    async (
      decision: 'approved' | 'rejected',
      override?: Record<string, unknown>,
    ): Promise<void> => {
      const row = pendingRowRef.current;
      const ctx = loopRef.current;
      if (row === null || ctx === null) {
        return;
      }
      const gen = ctx.gen;
      setGateBusy(true);
      try {
        const bare = bareToolName(row.tool_name);
        await applyDecision(row, decision, override);
        // A reset/new-run while the decision was in flight invalidates us.
        if (isStale(gen)) {
          return;
        }
        pendingRowRef.current = null;
        dispatch({ type: 'GATE_CLEARED' });
        if (decision === 'rejected') {
          // A rejection ends the operator's involvement; we stop driving
          // (no auto-resume) and surface a clear "stopped" terminal state.
          dispatch({ type: 'DONE', result: { kind: 'rejected', tool: bare } });
          return;
        }
        // The human's review time is NOT agent time: give the resumed run a
        // fresh agent-progress budget so a long approval pause can't make the
        // next poll instantly time out.
        ctx.deadline = Date.now() + MAX_RUN_MS;
        await pump();
      } catch (err) {
        if (isStale(gen)) {
          return;
        }
        dispatch({ type: 'ERROR', reason: (err as Error).message ?? 'approval failed' });
      } finally {
        if (!isStale(gen)) {
          setGateBusy(false);
        }
      }
    },
    [applyDecision, pump, isStale],
  );

  const uploadFile = useCallback(async (file: File): Promise<void> => {
    // New upload identity — supersedes any in-flight upload + any prior
    // successful file, so a stale completion can't repopulate `uploadedFile`.
    const gen = ++uploadGenRef.current;
    const current = (): boolean => mountedRef.current && uploadGenRef.current === gen;
    setUploadError(undefined);
    setUploadedFile(undefined);
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      const res = await retryableRefreshFetch(globalThis.fetch.bind(globalThis))(
        bffEndpoints().uploadUrl,
        {
          method: 'POST',
          body: form,
          credentials: 'include',
          headers: { 'X-Requested-With': 'fetch' },
        },
      );
      if (!current()) {
        return;
      }
      if (!res.ok) {
        let msg = `Upload failed (${res.status})`;
        try {
          const body = (await res.json()) as { message?: string; error?: string };
          msg = body.message ?? body.error ?? msg;
        } catch {
          // ignore parse failure
        }
        if (current()) {
          setUploadError(msg);
        }
        return;
      }
      const body = (await res.json()) as { id?: unknown };
      if (!current()) {
        return;
      }
      if (typeof body.id !== 'string' || body.id.length === 0) {
        setUploadError('Upload succeeded but no file id was returned.');
        return;
      }
      setUploadedFile({ id: body.id, name: file.name });
    } catch (err) {
      if (current()) {
        setUploadError((err as Error).message ?? 'Upload failed');
      }
    } finally {
      if (current()) {
        setUploading(false);
      }
    }
  }, []);

  const reset = useCallback((): void => {
    // Bump the generations so any in-flight loop iteration OR upload sees
    // itself as stale after its next await and bails without dispatching.
    runGenRef.current += 1;
    uploadGenRef.current += 1;
    loopRef.current = null;
    pendingRowRef.current = null;
    setGateBusy(false);
    setUploadError(undefined);
    setUploadedFile(undefined);
    // Bumping `uploadGenRef` makes any in-flight upload's `finally` skip its
    // own `setUploading(false)` (it's now stale) — so clear the busy flag
    // here, else the intake form would wedge in "Uploading…".
    setUploading(false);
    dispatch({ type: 'RESET' });
  }, []);

  return {
    state,
    uploading,
    uploadError,
    uploadedFile,
    gateBusy,
    uploadFile: (f) => void uploadFile(f),
    start: (i) => void start(i),
    resolveGate: (d, o) => void resolveGate(d, o),
    reset,
  };
}
